import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { DateRangeKey } from '@/api/dashboard';
import type { TimePeriod } from '@/data/mockPerformance';

/**
 * Shared period state across Home and Lens. Picking "Last 60 Days" on Home
 * lights up the same window on Lens (and vice-versa) without forcing a
 * second selection. Persisted to localStorage so the user's last choice
 * survives a refresh.
 */

export interface PeriodMeta {
  value: TimePeriod;
  label: string;
  api: DateRangeKey;
}

export const PERIODS: PeriodMeta[] = [
  { value: 30,  label: 'Last 30 Days',  api: '30_days'  },
  { value: 60,  label: 'Last 60 Days',  api: '60_days'  },
  { value: 90,  label: 'Last 90 Days',  api: '90_days'  },
  { value: 180, label: 'Last 6 Months', api: '180_days' },
  // "All Time" hidden for now. Re-add to restore it everywhere PERIODS renders.
  // A previously-persisted 730 value falls back to DEFAULT_PERIOD via isValidPeriod.
  // { value: 730, label: 'All Time',      api: '2_years'  },
];

const DEFAULT_PERIOD: TimePeriod = 30;
const STORAGE_KEY = 'kai.period.v1';

const isValidPeriod = (v: unknown): v is TimePeriod =>
  typeof v === 'number' && PERIODS.some((p) => p.value === v);

const loadInitial = (): TimePeriod => {
  if (typeof window === 'undefined') return DEFAULT_PERIOD;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw == null) return DEFAULT_PERIOD;
    const parsed = Number.parseInt(raw, 10);
    return isValidPeriod(parsed) ? parsed : DEFAULT_PERIOD;
  } catch {
    return DEFAULT_PERIOD;
  }
};

interface PeriodContextValue {
  period: TimePeriod;
  periodMeta: PeriodMeta;
  setPeriod: (value: TimePeriod) => void;
}

const PeriodContext = createContext<PeriodContextValue | null>(null);

export function PeriodProvider({ children }: { children: ReactNode }) {
  const [period, setPeriodState] = useState<TimePeriod>(loadInitial);

  // Persist on change so a hard refresh / new tab opens to the same window.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(period));
    } catch {
      // localStorage may be disabled (private mode, etc.) — ignore.
    }
  }, [period]);

  const setPeriod = useCallback((value: TimePeriod) => {
    setPeriodState(isValidPeriod(value) ? value : DEFAULT_PERIOD);
  }, []);

  const periodMeta = useMemo(
    () => PERIODS.find((p) => p.value === period) ?? PERIODS[0],
    [period],
  );

  const ctx = useMemo<PeriodContextValue>(
    () => ({ period, periodMeta, setPeriod }),
    [period, periodMeta, setPeriod],
  );

  return <PeriodContext.Provider value={ctx}>{children}</PeriodContext.Provider>;
}

export function usePeriod(): PeriodContextValue {
  const ctx = useContext(PeriodContext);
  if (!ctx) {
    throw new Error('usePeriod must be used inside <PeriodProvider>.');
  }
  return ctx;
}
